import * as swcHelpers from '@swc/helpers';
import { useState, useCallback, useEffect, useMemo } from 'react';
import '@grandvisionhq/utils';
import { usePaymentsUIContext } from '../../../context/PaymentsUI/context.js';
import '@paypal/react-paypal-js';
import '@grandvisionhq/state';
import '../../../models/adyen/payment-type.js';
import '../../../models/cart-payment-methods/cart-payment-methods.js';
import '../../../models/googlePay/types.js';
import { PaymentFlow } from '../../../models/payment/types.js';
import '../../../models/paypal/context.js';

const useQrPaymentState = ({ paymentState , orderId , error , paymentResponse , orderToken , onQrCodeRefresh , refetch , paymentFlow , cart , getNewCart ,  })=>{
    const [isQrCodeExpired, setIsQrCodeExpired] = useState(false);
    const [isQrRefreshLoading, setIsQrRefreshLoading] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [qrCodeExpireIn, setQrCodeExpireIn] = useState(0);
    const [externalUrl, setExternalUrl] = useState('');
    const { setPaymentQRCodeState , isQrCodeModalOpen , setIsQrCodeModalOpen  } = usePaymentsUIContext();
    /*
   *  When user cancels payment or payment fails
   *  actions: 1. Replicate
   *           2. Call refetch
   *           4. Close QR modal
   */ const cancelPayment = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (!(cart === null || cart === void 0 ? void 0 : cart.id)) {
            console.error('Need cart id to recover');
            setIsQrCodeModalOpen(false);
            return;
        }
        try {
            const cartResponse = yield getNewCart(cart === null || cart === void 0 ? void 0 : cart.id);
            if (!cartResponse.success) {
                throw 'Failed to recreate cart';
            }
            cartResponse.cartId && refetch(cartResponse.cartId, cartResponse.cartHash);
        } catch (e) {
            console.error('Error:', e);
        }
        setIsQrCodeModalOpen(false);
    }), [
        cart === null || cart === void 0 ? void 0 : cart.id,
        getNewCart,
        refetch,
        setIsQrCodeModalOpen
    ]);
    /*
   *  Listen for QR code payment status
   *  if paymentState is updated set paymentQRCodeState in PaymentsUI Context
   *  if PaymentState is 'Failed' or any error occurred, replicate and restore cart
   */ useEffect(()=>{
        if (paymentState) {
            setPaymentQRCodeState(paymentState);
        }
        if (paymentState === 'Failed' || error) {
            cancelPayment();
        }
    }, [
        paymentState,
        error,
        cancelPayment,
        orderId,
        orderToken,
        setPaymentQRCodeState
    ]);
    /*
   *  Reset QR code timeout
   *  Refresh QR code image
   */ useEffect(()=>{
        if (qrCodeExpireIn) {
            const timeoutId = setTimeout(()=>{
                setIsQrCodeExpired(true);
                setQrCodeExpireIn(0);
            }, qrCodeExpireIn);
            return ()=>clearTimeout(timeoutId)
            ;
        }
    }, [
        qrCodeExpireIn
    ]);
    /*
   *  Set/Update properties when QR code is updated
   */ useEffect(()=>{
        setQrCodeExpireIn(Number(paymentResponse.qrCodeExpiresIn) * 1000);
        setQrCode(String(paymentResponse.qrCodeUrl));
        setExternalUrl(String(paymentResponse.redirectUrl));
        setIsQrCodeExpired(false);
        setIsQrRefreshLoading(false);
    }, [
        paymentResponse.qrCodeExpiresIn,
        paymentResponse.qrCodeUrl,
        paymentResponse.redirectUrl
    ]);
    useEffect(()=>{
        if (paymentFlow === PaymentFlow.QR_CODE) {
            setIsQrCodeModalOpen(true);
        }
    }, [
        paymentFlow,
        setIsQrCodeModalOpen
    ]);
    /*
   *  On refreshQr replicate cart
   *  Get latest QR code
   */ const handleRefreshQrCode = useCallback(swcHelpers.asyncToGenerator(function*() {
        if (cart && orderId) {
            setQrCodeExpireIn(0);
            setIsQrCodeExpired(true);
            yield getNewCart(orderId);
            onQrCodeRefresh();
        }
        setIsQrRefreshLoading(true);
    }), [
        cart,
        onQrCodeRefresh,
        orderId,
        getNewCart
    ]);
    return useMemo(()=>({
            externalUrl,
            isOpen: isQrCodeModalOpen,
            isQrCodeExpired,
            isQrRefreshLoading,
            onCancel: ()=>cancelPayment()
            ,
            qrCodeUrl: qrCode,
            refreshQrCode: ()=>handleRefreshQrCode()
        })
    , [
        cancelPayment,
        externalUrl,
        handleRefreshQrCode,
        isQrCodeExpired,
        isQrCodeModalOpen,
        isQrRefreshLoading,
        qrCode, 
    ]);
};

export { useQrPaymentState };
//# sourceMappingURL=use-qr-payment-state.js.map
